import { ApolloClient, InMemoryCache, from, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { TokenRefreshLink } from 'apollo-link-token-refresh'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import store from '@/packages/vuex'
import { SET_MFA_STATUS } from '@/store/modules/Auth/mutation-types'
import { jwtDecode } from 'jwt-decode'
import Logger from '@/packages/logger'
import { isExpectedBackendError } from './errors'
import { MFA_ENROLLMENT_REQUIRED } from '@app/Exceptions'

// Keys that must never reach Sentry/LogRocket contexts — TOTP secrets,
// verification codes, Cognito session keys and tokens.
const SENSITIVE_LOG_KEYS = /^(parameters|sessionKey|secretCode|userCode|softwareToken|newPassword|password|confirmationCode|email|tokens|accessToken|idToken|refreshToken)$/i

function sanitizeForLogging (value) {
	if (!value || typeof value !== 'object') {
		return value
	}
	const clone = JSON.parse(JSON.stringify(value))
	const scrub = obj => {
		if (!obj || typeof obj !== 'object') {
			return
		}
		Object.keys(obj).forEach(key => {
			if (SENSITIVE_LOG_KEYS.test(key)) {
				obj[key] = '[REDACTED]'
			} else if (typeof obj[key] === 'object') {
				scrub(obj[key])
			}
		})
	}
	scrub(clone)
	return clone
}

// The backend requires MFA enrollment before protected operations are
// allowed again. Move the client into the restricted enrollment state and
// navigate to the setup page. A full reload (same pattern as the token
// refresh failure handler below) is used because the store's internal _vm
// has no router instance attached.
function handleMfaEnrollmentRequired () {
	const current = store.getters['Auth/mfa'] || {}
	store.commit(`Auth/${SET_MFA_STATUS}`, {
		...current,
		required: true,
		enforcementRequired: true
	})

	if (window.location.pathname !== '/auth/mfa/setup') {
		window.location.href = `/auth/mfa/setup?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`
	}
}

function logExpectedError (error, context) {
	if (process.env.NODE_ENV === 'production') {
		const message = error?.message || String(error)
		Logger.captureMessage(`Expected backend/integration error: ${message}`, {
			...context,
			expected_backend_error: true
		})
	} else {
		console.debug('Expected backend/integration error suppressed:', error, context)
	}
}


const fetcher = (...args) => {
	return window.fetch(...args)
}

const resolveWebSocketUrl = () => {
	const fallbackUrl = 'ws://localhost:6062'
	const configuredUrl = process.env.VUE_APP_WEBSOCKET_URL || fallbackUrl

	try {
		const parsedUrl = new URL(configuredUrl, window.location.origin)
		const protocol = parsedUrl.protocol === 'https:'
			? 'wss:'
			: parsedUrl.protocol === 'http:'
				? 'ws:'
				: parsedUrl.protocol

		const hostname = parsedUrl.hostname === '0.0.0.0'
			? window.location.hostname
			: parsedUrl.hostname

		return `${protocol}//${hostname}${parsedUrl.port ? `:${parsedUrl.port}` : ''}${parsedUrl.pathname}${parsedUrl.search}`
	} catch (_error) {
		return fallbackUrl
	}
}

const websocketRetryAttempts = process.env.NODE_ENV === 'production' ? 20 : 3


const uploadLink = createUploadLink({
	uri: process.env.VUE_APP_GRAPHQL_SERVER,
	fetch: fetcher,
	credentials: 'include'
})

// Create client instance first so we can reference it in links
let apolloClient

const createApolloLogContext = (operation, extra = {}) => ({
	operationName: operation?.operationName,
	variables: JSON.stringify(sanitizeForLogging(operation?.variables || {})),
	...extra
})

const createGraphQLErrorForLogging = (error, operation) => {
	const formattedError = new Error(error?.message || 'GraphQL error')
	formattedError.name = error?.name || 'GraphQLError'

	return {
		error: formattedError,
		context: createApolloLogContext(operation, {
			path: Array.isArray(error?.path) ? error.path.join('.') : error?.path,
			extensions: JSON.stringify(sanitizeForLogging(error?.extensions || {})),
			locations: JSON.stringify(error?.locations || []),
			response: JSON.stringify(sanitizeForLogging(error))
		})
	}
}

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
	if (graphQLErrors) {
		if (graphQLErrors.some(error => error?.code === MFA_ENROLLMENT_REQUIRED)) {
			handleMfaEnrollmentRequired()
		}
		graphQLErrors.forEach(error => {
			const { error: formattedError, context } = createGraphQLErrorForLogging(error, operation)
			if (isExpectedBackendError(formattedError)) {
				logExpectedError(formattedError, context)
			} else {
				Logger.captureException(formattedError, context)
			}
		})
		// return forward(operation) // tries refetch on failure
	}

	if (networkError) {
		const logContext = createApolloLogContext(operation)
		if (isExpectedBackendError(networkError)) {
			logExpectedError(networkError, logContext)
		} else {
			Logger.captureException(networkError, logContext)
		}

		// Reset cache on critical failures to prevent corruption
		if (apolloClient && (networkError.statusCode >= 500 || networkError.statusCode === 401)) {
			apolloClient.cache.reset().catch(err =>
				console.warn('Cache reset failed:', err)
			)
		}
	}
})


const authLink = setContext((_, { headers }) => {
	const accessToken = localStorage.getItem(process.env.VUE_APP_ACCESS_TOKEN_NAME)

	return {
		headers: {
			...headers,
			...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
		}
	}
})


const tokenRefreshLink = new TokenRefreshLink({
	accessTokenField: 'tokens',
	isTokenValidOrUndefined: () => {
		return new Promise((resolve) => {
			try {
				const accessToken = localStorage.getItem(
					process.env.VUE_APP_ACCESS_TOKEN_NAME
				)
				if (accessToken) {
					const decoded = jwtDecode(accessToken)
					resolve(!(decoded.exp - Date.now() / 1000 < 1))
				} else {
					resolve(true)
				}
			} catch (error) {
				resolve(false)
			}
		})
	},
	fetchAccessToken: () => {
		const body = JSON.stringify({
			operationName: 'RefreshTokens',
			query: `
                mutation RefreshTokens ($payload: RefreshTokensInput!) {
                    refreshTokens (payload: $payload) {
                        tokens {
                            accessToken
                            refreshToken
                            idToken
                        }
                    }
                }
            `,
			variables: {
				payload: {
					refreshToken: localStorage.getItem(
						process.env.VUE_APP_REFRESH_TOKEN_NAME
					)
				}
			}
		})
		return fetch(process.env.VUE_APP_GRAPHQL_SERVER, {
			body,
			method: 'POST',
			headers: { 'Content-Type': 'application/json' }
		})
	},
	handleResponse: () => async response => {
		const { data } = await response.json()
		return data.refreshTokens
	},
	handleFetch: tokens => {
		store.commit('Auth/SET_TOKENS', tokens)
	},
	handleError: () => {
		// Clear corrupted cache before redirect
		if (apolloClient) {
			apolloClient.cache.reset()
		}

		localStorage.removeItem(process.env.VUE_APP_ACCESS_TOKEN_NAME)
		localStorage.removeItem(process.env.VUE_APP_REFRESH_TOKEN_NAME)
		localStorage.removeItem(process.env.VUE_APP_ID_TOKEN_NAME)
		window.location.href = `/auth/login?redirect=${window.location.pathname}`
	}
})


// Create WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(
	createClient({
		url: resolveWebSocketUrl(),
		connectionParams: () => {
			// This function is called for each connection/reconnection
			// to get fresh authentication token
			const accessToken = localStorage.getItem(process.env.VUE_APP_ACCESS_TOKEN_NAME)
			return {
				authorization: accessToken ? `Bearer ${accessToken}` : ''
			}
		},
		shouldRetry: () => true,
		retryAttempts: websocketRetryAttempts,
		keepAlive: 10000, // Send keepalive pings every 10 seconds
		onConnected: () => {
			store.commit('WebSocket/SET_SUBSCRIPTION_READY', true)
		},
		onDisconnected: () => {
			store.commit('WebSocket/SET_SUBSCRIPTION_READY', false)
		}
	})
)


// Split link: send subscriptions to WebSocket, queries/mutations to HTTP
const httpLink = from([errorLink, tokenRefreshLink, authLink, uploadLink])

const splitLink = split(
	({ query }) => {
		const definition = getMainDefinition(query)
		return (
			definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
		)
	},
	wsLink,
	httpLink
)


export default new ApolloClient({
	link: splitLink,
	cache: new InMemoryCache({
		possibleTypes: {
			Notification: [
				'EbayTokenInvalidNotification',
				'PartnershipActionNotification',
				'BulkUpdateNotification'
			]
		},
		typePolicies: {
			PaginatedCollection: {
				keyFields: false,
				merge: true
			},
			PaginatedClaim: {
				keyFields: false,
				merge: true
			},
			PaginatedCategorySet: {
				keyFields: false,
				merge (_existing, incoming) {
					return incoming
				}
			},
			Query: {
				fields: {
					groupedSaleDraftItems: {
						merge (existing, incoming) {
							return incoming
						}
					},
					departmentStatistics: {
						merge (existing, incoming) {
							return incoming
						}
					},
					prevDepartmentStatistics: {
						merge (existing = {}, incoming = {}) {
							return {
								...existing,
								...incoming
							}
						}
					},
					billingDebetasDimensionDetails: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					userStatistics: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					tasks: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					notifications: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					mfaStatus: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					axleConfigurations: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					bodyTypes: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					colors: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					coolingTypes: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					driveTypes: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					fuelSystems: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					fuelTypes: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					gearTypes: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					platformTypes: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					steeringTypes: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					suspensionTypes: {
						merge (_existing, incoming) {
							return incoming
						}
					}
				}
			},
			Order: {
				fields: {
					parts: {
						merge (existing, incoming) {
							return incoming
						}
					}
				}
			},
			BillingRecord: {
				fields: {
					receiverDetails: {
						merge (_existing, incoming) {
							return incoming
						}
					}
				}
			},
			GroupedSaleDraftCategoryNode: {
				fields: {
					children: {
						merge (_existing, incoming) {
							return incoming
						}
					},
					items: {
						merge (_existing, incoming) {
							return incoming
						}
					}
				}
			},
			BillingDocumentConfig: {
				fields: {
					buyerConfigs: {
						merge (existing = [], incoming = [], { mergeObjects }) {
							const existingBuyerConfigs = Array.isArray(existing) ? existing : []
							const incomingBuyerConfigs = Array.isArray(incoming) ? incoming : []

							return incomingBuyerConfigs.map(incomingBuyerConfig => {
								const existingBuyerConfig = existingBuyerConfigs.find(existingItem => existingItem?.id === incomingBuyerConfig?.id)
								return existingBuyerConfig ? mergeObjects(existingBuyerConfig, incomingBuyerConfig) : incomingBuyerConfig
							})
						}
					},
					sellerConfigs: {
						merge (existing = [], incoming = [], { mergeObjects }) {
							const existingSellerConfigs = Array.isArray(existing) ? existing : []
							const incomingSellerConfigs = Array.isArray(incoming) ? incoming : []

							return incomingSellerConfigs.map(incomingSellerConfig => {
								const existingSellerConfig = existingSellerConfigs.find(existingItem => existingItem?.id === incomingSellerConfig?.id)
								return existingSellerConfig ? mergeObjects(existingSellerConfig, incomingSellerConfig) : incomingSellerConfig
							})
						}
					}
				}
			}
		}
	}),
	defaultOptions: {
		query: {
			fetchPolicy: 'network-only',
			errorPolicy: 'all'
		},
		watchQuery: {
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all'
		}
	}
})


